import { useCallback, useEffect, useMemo, useState } from "react";
import { getInvoices } from "../../services/invoices";

const buildParams = (page, size, filters) => {
  const params = { page, pageSize: size };
  if (filters.code) {
    const parts = String(filters.code)
      .split("-")
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length > 0) {
      params.codes = parts;
    }
  }
  
  if (filters.date) params.date = filters.date;
  if (filters.client) params.customer = filters.client;
  if (filters.phone) params.phone = filters.phone;
  if (filters.address) params.address = filters.address;
  if (filters.user) params.user = filters.user;
  if (filters.representative) params.representative = filters.representative;
  if (filters.status) params.status = filters.status;
  if (filters.quantity) params.quantity = Number(filters.quantity);
  if (filters.governorate) params.governorate = filters.governorate;
  return params;
};

export default function useInvoicesSearch(initialPageSize = 10) {
  const [filters, setFilters] = useState({
    code: "",
    date: "",
    client: "",
    phone: "",
    address: "",
    user: "",
    representative: "",
    status: "",
    quantity: "",
    governorate: "",
  });
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [invoicesData, setInvoicesData] = useState({
    data: [],
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: initialPageSize,
    hasPreviousPage: false,
    hasNextPage: false,
    message: "",
    statusCode: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchInvoices = useCallback(async (page = 1, overrideFilters) => {
    const activeFilters = overrideFilters || filters;
    const params = buildParams(page, pageSize, activeFilters);
    setIsLoading(true);
    try {
      const api = await getInvoices(params);
      setInvoicesData({
        data: api.data || [],
        totalCount: api.totalCount || 0,
        totalPages: api.totalPages || 1,
        currentPage: api.currentPage || page,
        pageSize: api.pageSize || pageSize,
        hasPreviousPage: Boolean(api.hasPreviousPage),
        hasNextPage: Boolean(api.hasNextPage),
        message: api.message,
        statusCode: api.statusCode,
      });
    } catch (error) {
      setInvoicesData((prev) => ({
        ...prev,
        data: [],
        totalCount: 0,
        totalPages: 1,
        currentPage: page,
        pageSize,
        hasPreviousPage: false,
        hasNextPage: false,
        message: error?.response?.data?.message || "حدث خطأ أثناء تحميل البيانات",
        statusCode: error?.response?.status || 0,
      }));
    } finally {
      setIsLoading(false);
    }
  }, [filters, pageSize]);

  useEffect(() => {
    fetchInvoices(1);
  }, [fetchInvoices]);

  const handleFilterChange = async (e) => {
    const { name, value } = e.target;
    const next = { ...filters, [name]: value };
    setFilters(next);
    await fetchInvoices(1, next);
  };

  const handleClearSearch = async () => {
    const cleared = {
      code: "",
      date: "",
      client: "",
      phone: "",
      address: "",
      user: "",
      representative: "",
      status: "",
      quantity: "",
      governorate: "",
    };
    setFilters(cleared);
    await fetchInvoices(1, cleared);
  };

  const handlePageChange = async (page) => {
    await fetchInvoices(page);
  };

  return {
    filters,
    setFilters,
    invoicesData,
    pageSize,
    setPageSize,
    fetchInvoices,
    handleFilterChange,
    handleClearSearch,
    handlePageChange,
    isLoading,
  };
} 